import { useContext, useEffect, useRef, useState } from "react";
import {
  Grid2,
  IconButton,
  Box,
  Typography,
  Button,
  Container,
  MenuItem,
} from "@mui/material";
import { CheckCircle } from "@mui/icons-material";
import Editicon from "../../../assets/images/Edit.png";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import { baseFetch } from "../../../helpers/apiClient";
import { prepareEventMedia } from "../../../helpers/uploadEventFile";
import SuccessModal from "../../../components/SuccessModal";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toggleSessionModal } from "../../../redux/sessionSlice";
import { categoryList } from "../../../constants/categories";
import MenteeHeader from "../../../components/MenteeHeader";
import BackButton from "../../../components/BackButton";
import RoundedTextField from "../../../components/RoundedTextField";
import DeleteEventPopup from "../../../components/DeleteEventPopup";
import { formatEventType } from "../../../constants/RegionalLocationList";
import RoundedSelect from "../../../components/RoundedSelect";
import Background from "../../../components/Background";
import Loader from "../../../components/Loader";
import { DesktopTimePicker } from "@mui/x-date-pickers/DesktopTimePicker";
import { formatParticipant } from "./CreateEventFrom";
import DashboardFooter from "../../../components/Footer";
import ImageCropper from "../../../components/ImageCropper";
import EventSubmissionPopup from "../../../components/EventSubmissionPopup";
import FileUpload from "../../../components/FileUpload";
import { EventFormContext } from "../../../context/EventFormContext";
import { useSnackbar } from "../../../context/SnackbarContext";
import PostingLimitModal from "../../../components/PostingLimitModal";
import {
  timezoneOptions,
  isValidEventUrl,
  EVENT_URL_ERROR_MESSAGE,
} from "../../../helpers/constants";
import RichTextEditor from "../../../components/RichTextEditor";
import { styles } from "./style";

// The backend stores event dates as UTC instants; the calendar day a sponsor
// cares about is the LOCAL day in the event's own timezone. Loading the picker
// with moment.utc() shows the UTC day, which for a near-midnight event (e.g.
// 11 PM Mountain = next day in UTC) is off by one — and gets re-submitted wrong
// if the field is touched. Read the local day via the Intl API (no extra deps);
// fall back to the UTC day for any unrecognized/legacy timezone string.
const eventDateForPicker = (utcValue, timezone) => {
  if (!utcValue) return null;
  // Once the user picks a date, this value is the moment the picker handed back
  // (already the chosen calendar day, in browser-local time) — show it as-is.
  // Re-interpreting its instant in the event's timezone would shift the day for
  // sponsors east of that zone. Only an instant loaded from the API (a UTC
  // string/Date) needs converting to the event's local calendar day.
  if (moment.isMoment(utcValue)) return utcValue;
  try {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: timezone || "America/Toronto",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(new Date(utcValue));
    const part = (type) => parts.find((p) => p.type === type)?.value;
    return moment(
      `${part("year")}-${part("month")}-${part("day")}`,
      "YYYY-MM-DD",
    );
  } catch {
    return moment.utc(utcValue);
  }
};

export default function EditEventFrom() {
  const navigate = useNavigate();
  const params = useParams();
  const dispatch = useDispatch();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const role = localStorage.getItem("role");
  const [activeTab, setActiveTab] = useState("managecontent");
  const [imageCropped, setImageCropped] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const filter = queryParams.get("filter");
  const isEdit = queryParams.get("isEdit");
  const { showSnackbar } = useSnackbar();
  const { formData, setFormData, updatedFields, setUpdatedFields } =
    useContext(EventFormContext);
  const [openImageCropper, setOpenImageCropper] = useState(false);
  const [deleteEventId, setDeleteEventId] = useState("");
  const [loading, setLoading] = useState(false);
  const completedUploads = useRef(new WeakMap());
  const submissionController = useRef(null);
  useEffect(() => () => submissionController.current?.abort(), []);
  const [open, setOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [tempPreview, setTempPreview] = useState(null);
  const filteredList =
    filter === "Posted"
      ? categoryList.filter(
          (item) =>
            item.key === "videos-and-presentations" ||
            item.key === "articles-and-blogs",
        )
      : categoryList.slice(1);
  const [errorMsg, setErrorMsg] = useState("");
  const fileInputRef = useRef(null);
  const contentSizeInfo = useSelector(
    (state) => state?.profileSlice?.profile?.contentSizeInfo,
  );
  const [openPostLimitModal, setOpenPostLimitModal] = useState(false);
  const [eventSubmissionModal, setEventSubmissonModal] = useState(false);

  const handleFileChange = async (event) => {
    setOpenImageCropper(true);
    const images = event.target.files[0];
    setImageCropped(URL.createObjectURL(images));
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };
  const handleImageUpload = async (file) => {
    setLoading(true);
    setTempPreview(URL.createObjectURL(file));
    const formData = new FormData();
    formData.append("photo", file);
    const payload = formData;
    let photoUrl;
    await baseFetch({
      url: `/v1/web/upload/image`,
      method: "POST",
      body: payload,
    })
      .then((response) => {
        if (response.status) {
          console.log("url is", response.data.upload.url);
          setLoading(false);
          photoUrl = response.data.upload.url;
        }
      })
      .catch((err) => {
        setLoading(false);
        if (err === "Please authenticate") {
          dispatch(toggleSessionModal(false));
        }
      });
    setUpdatedFields({ ...updatedFields, eventPhoto: photoUrl });
    setOpenImageCropper(false);
  };

  const handleDialogClose = () => {
    setOpenImageCropper(false);
  };

  useEffect(() => {
    setFormData((prevFormData) => ({ ...prevFormData, ...updatedFields }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedFields]);
  const hasPreviewData = Boolean(formData.linkClicked);
  useEffect(() => {
    if (hasPreviewData) return;
    const controller = new AbortController();
    setLoading(true);
    baseFetch({
      url: `/v1/web/event/single/${params.id}`,
      method: "GET",
      signal: controller.signal,
    })
      .then((response) => {
        if (controller.signal.aborted) return;
        setFormData({
          eventTitle: response?.data?.event?.eventTitle,
          eventType: response?.data?.event?.eventType,
          eventFee: response?.data?.event?.eventFee,
          eventDateStart: response?.data?.event?.eventDateStart,
          eventDateEnd: response?.data?.event?.eventDateEnd,
          eventTimeStart: response?.data?.event?.eventTimeStart,
          eventTimeEnd: response?.data?.event?.eventTimeEnd,
          timezone: response?.data?.event?.timezone,
          eventUrl: response?.data?.event?.eventUrl,
          resourceFile: response?.data?.event?.resourceFile,
          resourceFileUrl: response?.data?.event?.resourceFile?.url,
          resourceFileName: response?.data?.event?.resourceFile?.fileName,
          resourceFileSize: response?.data?.event?.resourceFile?.fileSize,
          numberOfParticipants: response?.data?.event?.numberOfParticipants,
          prevParticipants: response?.data?.event?.numberOfParticipants,
          eventDescription: response?.data?.event?.eventDescription,
          photo: response?.data?.event?.eventPhoto,
        });
        setLoading(false);
        setOpen(false);
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        console.log("err", err);
        setLoading(false);
        if (err === "Please authenticate") {
          dispatch(toggleSessionModal(false));
        }
      });
    return () => controller.abort();
  }, [params.id, hasPreviewData, setFormData, dispatch]);

  const submitForm = async () => {
    if (submissionController.current) return;
    const controller = new AbortController();
    submissionController.current = controller;
    setLoading(true);
    setErrorMsg("");
    if (formData.eventType === "articles-and-blogs") {
      setEventSubmissonModal(false);
    }
    try {
      const fields = {};
      for (const [key, value] of Object.entries(updatedFields)) {
        if (key === "eventDateStart" || key === "eventDateEnd") {
          fields[key] = moment(value).format("YYYY-MM-DD");
        } else if (
          key !== "resourceFileUrl" &&
          key !== "resourceFileName" &&
          key !== "resourceFileSize"
        ) {
          fields[key] = value;
        }
      }
      if ((filter === "Posted" || filter === "Past") && !isEdit) {
        fields.duplicate = true;
      }
      const body = await prepareEventMedia(fields, completedUploads.current, {
        signal: controller.signal,
      });
      const response = await baseFetch({
        url: `/v1/web/event/update/${params.id}`,
        method: "PUT",
        signal: controller.signal,
        body,
      });
      if (response.status && response.message === "EventUpdatedSuccessful") {
        setOpen(true);
        setUpdatedFields({});
      }
    } catch (error) {
      if (!controller.signal.aborted) {
        showSnackbar({ message: error?.message ?? error });
      }
    } finally {
      submissionController.current = null;
      if (!controller.signal.aborted) setLoading(false);
    }
  };

  const handleSubmit = () => {
    console.log("event start date is", formData.eventDateStart);
    const today = moment().utc().startOf("day");
    if (showDateAndTime && moment(formData.eventDateStart).isBefore(today)) {
      setErrorMsg("Event Start Date must be in future");
    } else if (formData.numberOfParticipants > 100) {
      setErrorMsg("Maximum number of participants allowed is 100");
    } else if (
      formData.eventType === "articles-and-blogs" &&
      formData.eventDescription === ""
    ) {
      setEventSubmissonModal(true);
    } else if (
      filter === "Upcoming" &&
      formData?.prevParticipants > updatedFields?.numberOfParticipants
    ) {
      setErrorMsg("Number of participants can't be reduced");
    } else {
      submitForm();
    }
  };

  const handleDeleteEvent = () => {
    setLoading(true);
    baseFetch({
      url: `/v1/web/event/delete/${deleteEventId}`,
      method: "DELETE",
    })
      .then((response) => {
        console.log("response ~ ", response);
        //fetchEventList()
        navigate("/dashboard?active=managecontent", { state: filter });
        setLoading(false);
      })
      .catch((err) => {
        console.log("err", err);
        setLoading(false);
        if (err === "Please authenticate") {
          dispatch(toggleSessionModal(false));
        }
      });
  };
  const showDateAndTime =
    formData.eventType === "webinar" ||
    formData.eventType === "virtual-event" ||
    formData.eventType === "";
  let emptyFields;
  if (showDateAndTime) {
    if (
      updatedFields?.eventTitle === "" ||
      updatedFields?.eventDescription === "" ||
      updatedFields?.numberOfParticipants === "" ||
      updatedFields?.eventUrl === "" ||
      formData?.eventTitle === "" ||
      formData?.eventDescription === "" ||
      formData?.numberOfParticipants === 0 ||
      formData?.eventUrl === "" ||
      formData?.eventDateStart === "" ||
      formData?.eventDateEnd === "" ||
      formData?.eventTimeStart === "" ||
      formData?.eventTimeEnd === ""
    ) {
      emptyFields = true;
    } else {
      emptyFields = false;
    }
  } else if (!showDateAndTime) {
    if (formData.eventType === "articles-and-blogs") {
      if (updatedFields?.eventTitle === "" || formData?.eventTitle === "") {
        emptyFields = true;
      } else {
        emptyFields = false;
      }
    } else if (
      updatedFields?.eventTitle === "" ||
      updatedFields?.eventDescription === ""
    ) {
      emptyFields = true;
    } else {
      emptyFields = false;
    }
  }
  const showEventUrl =
    formData.eventType === "webinar" ||
    formData.eventType === "virtual-event" ||
    formData.eventType === "articles-and-blogs";
  console.log(formData.eventType, "form data event type");
  const disable =
    (filter != "Posted" && Object.keys(updatedFields).length === 0) ||
    emptyFields ||
    ((formData.eventType === "videos-and-presentations" ||
      formData.eventType === "articles-and-blogs") &&
      ((!formData.resourceFile && !formData.eventUrl) || emptyFields));

  const handleChangeActive = (param) => {
    setActiveTab(param);
  };
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleChange = (event) => {
    const { value } = event.target;
    if (value?.trim().split(/\s+/).filter(Boolean).length < 1001) {
      setUpdatedFields({
        ...updatedFields,
        eventDescription: event.target.value,
      });
    }
  };

  const handleConfirmLogout = () => {
    submitForm();
  };
  const handleLogoutClose = () => {
    setEventSubmissonModal(false);
  };
  const handleVideoChange = (file) => {
    //setFile(e.target.files[0])
    console.log("file in edit event form is", file);
    setFormData((prevFormData) => ({
      ...prevFormData,
      resourceFile: file,
      resourceFileUrl: URL.createObjectURL(file),
      resourceFileName: file.name,
      resourceFileSize: file.size,
    }));
    setUpdatedFields((prevUpdatedFields) => ({
      ...prevUpdatedFields,
      resourceFile: file,
      resourceFileUrl: URL.createObjectURL(file),
      resourceFileName: file.name,
      resourceFileSize: file.size,
    }));
  };
  console.log("form data in edit event form is", formData);
  return (
    <>
      <MenteeHeader
        role={role}
        active={activeTab}
        searchTerm={searchTerm}
        handleChangeActive={handleChangeActive}
        handleSearchChange={handleSearchChange}
      />
      <Background>
        {loading && <Loader />}
        <Box>
          <Container maxWidth="xl">
            <BackButton
              leftNormal={true}
              onBackStep={() => {
                navigate("/dashboard?active=managecontent", { state: filter });
              }}
            />
            <Box
              sx={{
                paddingTop: {
                  xs: "15px",
                  sm: "30px",
                },
                paddingBottom: "45px",
              }}
              className="dashboard-main-div"
            >
              <Box>
                <Grid2 container spacing={4}>
                  <Grid2
                    item
                    size={{ xs: 12, sm: 11, md: 10, lg: 9 }}
                    sx={{ margin: "auto" }}
                  >
                    <Box
                      sx={{
                        background: "#FFFFFF",
                        padding: {
                          xs: "15px",
                          sm: "20px",
                        },
                        borderRadius: "10px",
                        width: "100%",
                      }}
                    >
                      <Grid2 container spacing={4}>
                        <Grid2 item size={{ xs: 12, md: 6 }}>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "20px",
                            }}
                          >
                            <Box
                              sx={{
                                width: "100%",
                                height: {
                                  xs: "200px",
                                  sm: "200px",
                                  md: "300px",
                                },
                                borderRadius: "30px",
                                backgroundColor: "#F1F7FA",
                                position: "relative",
                                margin: "0 auto",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              {formData?.photo || tempPreview ? (
                                <img
                                  src={
                                    tempPreview ? tempPreview : formData?.photo
                                  }
                                  alt="Cover"
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    borderRadius: "30px",
                                    objectFit: "cover",
                                  }}
                                />
                              ) : (
                                <Box>
                                  <Box
                                    component="img"
                                    src="/images/icons/dummy-img-icon1.png"
                                    alt="profile icon"
                                    sx={{
                                      width: {
                                        xs: "50px",
                                        sm: "54px",
                                        md: "60px",
                                        lg: "60px",
                                        xl: "74px",
                                      },
                                      height: {
                                        xs: "50px",
                                        sm: "54px",
                                        md: "60px",
                                        lg: "60px",
                                        xl: "74px",
                                      },
                                    }}
                                  />
                                </Box>
                              )}

                              <IconButton
                                sx={{
                                  position: "absolute",
                                  bottom: "10px",
                                  right: "10px",
                                  backgroundColor: "#2A4876",
                                  color: "#FFF",
                                  width: "32px",
                                  height: "32px",
                                  "&:hover": {
                                    backgroundColor: "#1E3863",
                                  },
                                }}
                                component="label"
                              >
                                <input
                                  name="photo"
                                  hidden
                                  accept="image/jpg,image/jpeg,image/png"
                                  type="file"
                                  onChange={handleFileChange}
                                  ref={fileInputRef}
                                />
                                <img width={"14px"} src={Editicon} />
                              </IconButton>
                              {imageCropped && (
                                <ImageCropper
                                  open={openImageCropper}
                                  image={imageCropped}
                                  onCrop={handleImageUpload}
                                  onCancel={handleDialogClose}
                                  loading={loading}
                                  aspectRatio={16 / 9}
                                />
                              )}
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "10px",
                              }}
                            >
                              <Typography
                                mb={0.5}
                                sx={{
                                  fontSize: "14px",
                                  textAlign: "left",
                                }}
                                component="dt"
                              >
                                Title
                              </Typography>
                              <RoundedTextField
                                fullWidth
                                name="eventTitle"
                                size="small"
                                placeholder="Enter here"
                                value={
                                  updatedFields?.eventTitle ??
                                  formData.eventTitle ??
                                  ""
                                }
                                onChange={(e) =>
                                  setUpdatedFields({
                                    ...updatedFields,
                                    eventTitle: e.target.value,
                                  })
                                }
                                sx={{
                                  marginBottom: "0px",
                                  borderRadius: "50px",
                                  backgroundColor: "#F2F7FB",
                                  textTransform: "capitalize",
                                  "& .MuiInputBase-input": {
                                    color: "#2A4876",
                                  },
                                }}
                              />
                            </Box>

                            <Typography
                              mb={0.5}
                              sx={{
                                fontSize: "14px",
                                textAlign: "left",
                              }}
                              component="dt"
                            >
                              Select Content Type
                            </Typography>
                            <RoundedSelect
                              sx={{
                                textTransform: "normal",
                                "& .MuiInputBase-input": {
                                  color: "#2A4876",
                                },
                              }}
                              disabled={
                                filter === "Upcoming" ||
                                (filter === "Posted" && isEdit)
                              }
                              value={
                                formData.eventType !== "" &&
                                formatEventType("key", formData.eventType)
                              }
                              onChange={(e) => {
                                if (
                                  e.target.value != "Articles and Blogs" &&
                                  e.target.value !=
                                    "Videos and Presentations" &&
                                  contentSizeInfo?.notAllowedContent?.length > 0
                                ) {
                                  setOpenPostLimitModal(true);
                                } else {
                                  setUpdatedFields({
                                    ...updatedFields,
                                    eventType: formatEventType(
                                      "value",
                                      e.target.value,
                                    ),
                                    resourceFile: null,
                                    eventUrl:
                                      e.target.value ===
                                      "Videos and Presentations"
                                        ? ""
                                        : formData?.eventUrl,
                                  });
                                }
                              }}
                              displayEmpty
                              renderValue={(type) => {
                                // Show placeholder when no value is selected
                                if (!type) {
                                  return (
                                    <span style={{ color: "#AAB6C8" }}>
                                      Select
                                    </span>
                                  );
                                }
                                return type;
                              }}
                              inputProps={{ "aria-label": "Without label" }} // Remove default label
                            >
                              {filteredList.map((category) => (
                                <MenuItem
                                  key={category.key}
                                  value={category.value}
                                >
                                  {category.value}{" "}
                                  {formatEventType(
                                    "key",
                                    formData.eventType,
                                  ) === category.value && (
                                    <CheckCircle
                                      sx={{
                                        marginLeft: "auto",
                                        color: "#2A4876",
                                        width: "14px",
                                      }}
                                    />
                                  )}
                                </MenuItem>
                              ))}
                            </RoundedSelect>
                            {showDateAndTime && (
                              <>
                                <Grid2 container spacing={2}>
                                  <Grid2
                                    sx={{ marginBottom: -1 }}
                                    item
                                    size={{ xs: 12, md: 12 }}
                                  >
                                    <Typography
                                      mb={0}
                                      sx={{
                                        fontSize: "14px",
                                        textAlign: "left",
                                      }}
                                      component="dt"
                                    >
                                      {formData?.eventType === "virtual-event"
                                        ? "Event Date"
                                        : "Date"}
                                    </Typography>
                                  </Grid2>

                                  <Grid2 item size={{ xs: 12 }}>
                                    <LocalizationProvider
                                      dateAdapter={AdapterMoment}
                                    >
                                      <DatePicker
                                        value={
                                          formData.eventDateStart
                                            ? eventDateForPicker(
                                                formData.eventDateStart,
                                                formData.timezone,
                                              )
                                            : null
                                        }
                                        format="MMM DD, YYYY"
                                        slotProps={{
                                          textField: {
                                            placeholder: "Start Date",
                                          },
                                        }}
                                        onChange={(date) =>
                                          setUpdatedFields({
                                            ...updatedFields,
                                            eventDateStart: date,
                                            eventDateEnd: date,
                                          })
                                        }
                                        minDate={
                                          filter === "Upcoming"
                                            ? moment().add(7, "days")
                                            : moment().add(16, "days")
                                        }
                                        sx={{
                                          borderRadius: "50px", // Rounded corners
                                          backgroundColor: "#F2F7FB", // Light background color
                                          padding: "0px 14px",
                                          textTransform: "capitalize",
                                          "& .MuiInputBase-input": {
                                            color: "#2A4876",
                                          },
                                          width: "100%", // Inner padding for better alignment
                                          "& fieldset": {
                                            border: "none", // Subtle border
                                          },
                                          "&:hover fieldset": {
                                            border: "none", // Darker border on hover
                                          },
                                          "&.Mui-focused fieldset": {
                                            border: "none", // Highlighted border on focus
                                          },
                                        }}
                                      />
                                    </LocalizationProvider>
                                  </Grid2>
                                </Grid2>
                                <Grid2 container spacing={2}>
                                  <Grid2
                                    sx={{ marginBottom: -1 }}
                                    item
                                    size={{ xs: 12, md: 12 }}
                                  >
                                    <Typography
                                      sx={styles.labelText}
                                      component="dt"
                                    >
                                      Timezone
                                    </Typography>
                                    <RoundedSelect
                                      value={formData.timezone}
                                      onChange={(e) =>
                                        setUpdatedFields({
                                          ...updatedFields,
                                          timezone: e.target.value,
                                        })
                                      }
                                      placeholder="Select Timezone"
                                      displayEmpty
                                      renderValue={(selected) => {
                                        if (!selected)
                                          return (
                                            <span style={{ color: "#AAB6C8" }}>
                                              Select Timezone
                                            </span>
                                          );
                                        const selectedOption =
                                          timezoneOptions.find(
                                            (t) => t.value === selected,
                                          );
                                        return selectedOption
                                          ? selectedOption.label
                                          : selected;
                                      }}
                                      inputProps={{
                                        "aria-label": "timezone-select",
                                      }}
                                      sx={styles.selectField}
                                    >
                                      {timezoneOptions.map((tz) => (
                                        <MenuItem
                                          key={tz.value}
                                          value={tz.value}
                                        >
                                          {tz.label}
                                          {formData.timezone === tz.value && (
                                            <CheckCircle
                                              sx={{
                                                marginLeft: "auto",
                                                color: "#2A4876",
                                                width: "14px",
                                              }}
                                            />
                                          )}
                                        </MenuItem>
                                      ))}
                                    </RoundedSelect>
                                  </Grid2>
                                </Grid2>
                                <Grid2 container spacing={2}>
                                  <Grid2
                                    sx={{ marginBottom: -1 }}
                                    item
                                    size={{ xs: 12, md: 12 }}
                                  >
                                    <Typography
                                      mb={0}
                                      sx={{
                                        fontSize: "14px",
                                        textAlign: "left",
                                      }}
                                      component="dt"
                                    >
                                      {formData?.eventType === "virtual-event"
                                        ? "Event Time"
                                        : "Time"}
                                    </Typography>
                                  </Grid2>
                                  <Grid2 item size={{ xs: 12, md: 6 }}>
                                    <LocalizationProvider
                                      dateAdapter={AdapterMoment}
                                    >
                                      <DesktopTimePicker
                                        value={
                                          formData.eventTimeStart
                                            ? moment(
                                                formData.eventTimeStart,
                                                "hh:mm A",
                                              )
                                            : null
                                        }
                                        slotProps={{
                                          textField: {
                                            placeholder: "Start Time",
                                          },
                                        }}
                                        onChange={(time) =>
                                          setUpdatedFields({
                                            ...updatedFields,
                                            eventTimeStart: time
                                              ? time.format("hh:mm A")
                                              : "",
                                            eventTimeEnd: time
                                              ? time.format("hh:mm A")
                                              : "",
                                          })
                                        }
                                        sx={{
                                          borderRadius: "50px", // Rounded corners
                                          backgroundColor: "#F2F7FB", // Light background color
                                          padding: "0px 14px",
                                          textTransform: "capitalize",
                                          width: "100%",
                                          "& .MuiInputBase-input": {
                                            color: "#2A4876",
                                          }, // Inner padding for better alignment
                                          "& fieldset": {
                                            border: "none", // Subtle border
                                          },
                                          "&:hover fieldset": {
                                            border: "none", // Darker border on hover
                                          },
                                          "&.Mui-focused fieldset": {
                                            border: "none", // Highlighted border on focus
                                          },
                                        }}
                                      />
                                    </LocalizationProvider>
                                  </Grid2>
                                  <Grid2 item size={{ xs: 12, md: 6 }}>
                                    <LocalizationProvider
                                      dateAdapter={AdapterMoment}
                                    >
                                      <DesktopTimePicker
                                        value={
                                          formData.eventTimeEnd
                                            ? moment(
                                                formData.eventTimeEnd,
                                                "hh:mm A",
                                              )
                                            : null
                                        }
                                        slotProps={{
                                          textField: {
                                            placeholder: "End Time",
                                          },
                                        }}
                                        minTime={
                                          formData.eventTimeStart
                                            ? moment(
                                                formData.eventTimeStart,
                                                "hh:mm A",
                                              )
                                            : null
                                        }
                                        onChange={(time) =>
                                          setUpdatedFields({
                                            ...updatedFields,
                                            eventTimeEnd: time
                                              ? time.format("hh:mm A")
                                              : "",
                                          })
                                        }
                                        sx={{
                                          borderRadius: "50px", // Rounded corners
                                          backgroundColor: "#F2F7FB", // Light background color
                                          padding: "0px 14px",
                                          textTransform: "capitalize",
                                          width: "100%",
                                          "& .MuiInputBase-input": {
                                            color: "#2A4876",
                                          }, // Inner padding for better alignment
                                          "& fieldset": {
                                            border: "none", // Subtle border
                                          },
                                          "&:hover fieldset": {
                                            border: "none", // Darker border on hover
                                          },
                                          "&.Mui-focused fieldset": {
                                            border: "none", // Highlighted border on focus
                                          },
                                        }}
                                      />
                                    </LocalizationProvider>
                                  </Grid2>
                                </Grid2>
                              </>
                            )}
                            <Button
                              onClick={() => {
                                setDeleteModalOpen(true);
                                setDeleteEventId(params.id);
                              }}
                              variant="contained"
                              sx={{
                                backgroundColor: "#fff",
                                color: "#2A4876",
                                textTransform: "none",
                                padding: "10px 20px",
                                borderRadius: "20px",
                                width: "100%",
                              }}
                            >
                              Delete
                            </Button>
                          </Box>
                        </Grid2>
                        <Grid2 item size={{ xs: 12, md: 6 }}>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "10px",
                            }}
                          >
                            <Typography
                              mb={0.5}
                              sx={{
                                fontSize: "14px",
                                textAlign: "left",
                              }}
                              component="dt"
                            >
                              {formData.eventType === "articles-and-blogs"
                                ? "Description (Optional)"
                                : "Description"}
                            </Typography>

                            <RichTextEditor
                              value={
                                updatedFields?.eventDescription ??
                                formData.eventDescription ??
                                ""
                              }
                              onChange={(value) =>
                                handleChange({
                                  target: { name: "eventDescription", value },
                                })
                              }
                              placeholder="Enter here..."
                              style={{
                                border: "none",
                                borderRadius: "50px",
                                backgroundColor: "#F2F7FB",
                                padding: "10px 15px",
                                minHeight: "300px",
                                color: "#2A4876",
                                // textTransform: 'capitalize',
                              }}
                              className="custom-quill"
                            />

                            <Typography
                              sx={{
                                position: "relative",
                                bottom: -6,
                                left: 10,
                                fontSize: "12px",
                                color: "gray",
                                px: 1,
                                "@media (max-width: 600px)": {
                                  fontSize: "11px",
                                  bottom: -5,
                                },
                                textAlign: "right",
                              }}
                            >
                              {`${
                                (
                                  updatedFields?.eventDescription ??
                                  formData.eventDescription ??
                                  ""
                                )
                                  .replace(/<[^>]*>/g, "") // strip HTML tags
                                  .trim()
                                  .split(/\s+/)
                                  .filter(Boolean).length
                              }/1000`}
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "10px",
                              marginTop: "15px",
                            }}
                          >
                            {showEventUrl && (
                              <>
                                <Typography
                                  mb={0.5}
                                  sx={{
                                    fontSize: "14px",
                                    textAlign: "left",
                                  }}
                                  component="dt"
                                >
                                  {formData?.eventType !== "virtual-event"
                                    ? "URL"
                                    : "Event URL"}
                                </Typography>
                                <RoundedTextField
                                  fullWidth
                                  size="small"
                                  name="eventUrl"
                                  onChange={(e) =>
                                    setUpdatedFields({
                                      ...updatedFields,
                                      eventUrl: e.target.value,
                                    })
                                  }
                                  placeholder="Enter here"
                                  value={
                                    updatedFields?.eventUrl ??
                                    formData?.eventUrl ??
                                    ""
                                  }
                                  disabled={formData?.resourceFile}
                                  error={
                                    !isValidEventUrl(
                                      updatedFields?.eventUrl ??
                                        formData?.eventUrl ??
                                        "",
                                    )
                                  }
                                  helperText={
                                    !isValidEventUrl(
                                      updatedFields?.eventUrl ??
                                        formData?.eventUrl ??
                                        "",
                                    )
                                      ? EVENT_URL_ERROR_MESSAGE
                                      : ""
                                  }
                                  sx={{
                                    marginBottom: "12px",
                                    borderRadius: "50px",
                                    backgroundColor: "#F2F7FB",
                                    textTransform: "capitalize",
                                    "& .MuiInputBase-input": {
                                      color: "#2A4876",
                                    },
                                  }}
                                />
                              </>
                            )}
                          </Box>
                          {!showDateAndTime && (
                            <FileUpload
                              eventType={formData.eventType}
                              formData={formData}
                              setFormData={setFormData}
                              handleVideoChange={handleVideoChange}
                              file={formData?.resourceFile}
                              edit={true}
                              eventId={params.id}
                              disabled={formData?.eventUrl}
                              hideDelete={isEdit}
                            />
                          )}
                          {showDateAndTime && (
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "10px",
                              }}
                            >
                              <Typography
                                mb={0.5}
                                sx={{
                                  fontSize: "14px",
                                  textAlign: "left",
                                }}
                                component="dt"
                              >
                                Number of Participants
                              </Typography>
                              <RoundedTextField
                                fullWidth
                                size="small"
                                name="numberOfParticipants"
                                onChange={(e) => {
                                  const inputValue = e.target.value;
                                  const numericValue = Number(inputValue);
                                  // Block 0 or negative values
                                  if (inputValue && numericValue === 0) return;
                                  setUpdatedFields({
                                    ...updatedFields,
                                    numberOfParticipants:
                                      formatParticipant(inputValue),
                                  });
                                }}
                                placeholder="Enter here (max 100)"
                                value={
                                  formData?.numberOfParticipants === 0
                                    ? ""
                                    : formData?.numberOfParticipants
                                }
                                sx={{
                                  marginBottom: "21px",
                                  borderRadius: "50px",
                                  backgroundColor: "#F2F7FB",
                                  textTransform: "capitalize",
                                  "& .MuiInputBase-input": {
                                    color: "#2A4876",
                                  },
                                }}
                              />
                            </Box>
                          )}
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "10px",
                            }}
                          >
                            <Button
                              variant="contained"
                              color="primary"
                              fullWidth
                              sx={{
                                marginTop: "10px",
                                height: {
                                  xs: "40px",
                                  sm: "45px",
                                  md: "45px",
                                  lg: "50px",
                                  xl: "60px",
                                },
                                width: "100% !important",
                                borderRadius: "100px",
                                textTransform: "capitalize",
                              }}
                              onClick={() => handleSubmit()}
                              style={{
                                backgroundColor:
                                  ((filter === "Posted" && !isEdit) ||
                                    filter === "Past") &&
                                  !disable
                                    ? "#7BB9D5"
                                    : !disable
                                      ? "#2A4876"
                                      : "#AAB6C8", // Change backgroundColor based on input values
                                color:
                                  ((filter === "Posted" && !isEdit) ||
                                    filter === "Past") &&
                                  !disable
                                    ? "#152134"
                                    : "#fff",
                                fontSize: "18px",
                                cursor: disable ? "not-allowed" : "pointer", // Disable hover effect when fields are not filled
                              }}
                              disabled={disable || loading} // Disable while media is uploading.
                            >
                              {(filter === "Posted" && !isEdit) ||
                              filter === "Past"
                                ? "Duplicate"
                                : "Save Changes"}
                            </Button>
                            {errorMsg && (
                              <Typography sx={{ color: "red" }}>
                                {errorMsg}
                              </Typography>
                            )}
                          </Box>
                        </Grid2>
                      </Grid2>
                    </Box>
                    {open && (
                      <SuccessModal
                        open={open}
                        onClose={() => {
                          setOpen(false);
                          navigate("/dashboard");
                        }}
                        title="Changes Saved Successfully!"
                      />
                    )}
                    {eventSubmissionModal && (
                      <EventSubmissionPopup
                        logoutDialogOpen={eventSubmissionModal}
                        handleConfirmLogout={handleConfirmLogout}
                        handleLogoutClose={handleLogoutClose}
                      />
                    )}
                    {openPostLimitModal && (
                      <PostingLimitModal
                        open={openPostLimitModal}
                        onClose={() => setOpenPostLimitModal(false)}
                        maxedOutContent={contentSizeInfo?.notAllowedContent}
                        allowedContent={contentSizeInfo?.allowedContent}
                      />
                    )}
                  </Grid2>
                </Grid2>
              </Box>
            </Box>
          </Container>
        </Box>
        {deleteModalOpen && (
          <DeleteEventPopup
            handleConfirm={() => handleDeleteEvent()}
            handleClose={() => setDeleteModalOpen(false)}
            open={deleteModalOpen}
          />
        )}
      </Background>
      <DashboardFooter />
    </>
  );
}
